import { logger } from '../../utils/logger';
import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Divider,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Tooltip,
  InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import HelpIcon from '@mui/icons-material/Help';
import taxService from '../../services/taxService';
import { ErrorDisplay } from '../../components/common/ErrorDisplay';

// Product categories and the tax code each one is calculated with
const CATEGORY_TAX_CODES = {
  general: 'txcd_99999999',   // General - Tangible Goods
  clothing: 'txcd_30011000',  // Clothing & Footwear
  food: 'txcd_40000000',      // Food & grocery
  digital: 'txcd_10302000',   // Digital products (downloaded)
  service: 'txcd_20030000',   // General - Services
  saas: 'txcd_10103001',      // SaaS - business use
};

const productCategories = [
  { value: 'general', label: 'General Merchandise' },
  { value: 'clothing', label: 'Clothing' },
  { value: 'food', label: 'Food & Grocery' },
  { value: 'digital', label: 'Digital Products' },
  { value: 'service', label: 'Services' },
  { value: 'saas', label: 'Software as a Service' }
];

// Initial form values
const initialValues = {
  transactionType: 'sale',
  transactionDate: new Date().toISOString().split('T')[0],
  shipFromAddress: {
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US'
  },
  shipToAddress: {
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US'
  },
  items: [
    {
      description: '',
      quantity: 1,
      unitPrice: '',
      productCategory: 'general',
      taxCode: '',
      discount: 0,
      taxable: true
    }
  ],
  shipping: 0,
  handling: 0,
  customerExempt: false,
  exemptionCertificate: ''
};

// Validation schema
const validationSchema = Yup.object({
  transactionDate: Yup.date().required('Date is required'),
  shipToAddress: Yup.object({
    street: Yup.string().required('Street address is required'),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    zipCode: Yup.string().required('ZIP code is required').matches(/^\d{5}(-\d{4})?$/, 'Invalid ZIP format'),
    country: Yup.string().required('Country is required')
  }),
  items: Yup.array().of(
    Yup.object({
      description: Yup.string().required('Description is required'),
      quantity: Yup.number().required('Quantity is required').positive('Must be positive'),
      unitPrice: Yup.number().required('Price is required').min(0, 'Price cannot be negative'),
      productCategory: Yup.string().required('Category is required')
    })
  ).min(1, 'At least one item is required'),
  shipping: Yup.number().min(0, 'Shipping cannot be negative'),
  handling: Yup.number().min(0, 'Handling cannot be negative')
});

const TaxCalculator = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);
      setResult(null);

      try {
        // Format line items for the tax engine
        const line_items = values.items.map(item => {
          // Make sure amount is a positive integer in cents
          const unitPrice = parseFloat(item.unitPrice) || 0;
          const quantity = parseInt(item.quantity) || 1;
          const discount = parseFloat(item.discount) || 0;
          
          // Calculate the item amount in cents (after discount)
          const itemAmount = Math.max(1, Math.round(quantity * unitPrice * (1 - discount / 100) * 100)); // Minimum 1 cent
          
          return {
            amount: itemAmount,
            quantity: quantity,
            tax_code: item.taxCode || CATEGORY_TAX_CODES[item.productCategory] || "txcd_99999999",
            reference: item.description || "Product",
            tax_behavior: "exclusive" // Required field
          };
        });

        // Calculate shipping and handling in cents
        const shippingAmount = parseFloat(values.shipping) || 0;
        const handlingAmount = parseFloat(values.handling) || 0;

        // Prepare data for the tax engine
        const calculationData = {
          currency: "usd",
          customer_details: {
            address: {
              line1: values.shipToAddress.street || "123 Main St", // Provide default if empty
              city: values.shipToAddress.city,
              state: values.shipToAddress.state,
              postal_code: values.shipToAddress.zipCode,
              country: values.shipToAddress.country || 'US'
            },
            address_source: "shipping",
            ip_address: null // Explicitly set to null to prevent IP-based detection
          },
          line_items: line_items
        };

        // Add shipping cost if any
        if (shippingAmount > 0) {
          calculationData.shipping_cost = {
            amount: Math.max(1, Math.round(shippingAmount * 100)), // Minimum 1 cent
            tax_code: "txcd_92010001", // Standard shipping tax code
            tax_behavior: "exclusive"
          };
        }

        // Add handling as a line item if any
        if (handlingAmount > 0) {
          calculationData.line_items.push({
            amount: Math.max(1, Math.round(handlingAmount * 100)), // Minimum 1 cent
            reference: "Handling Fee",
            tax_code: "txcd_92010001",
            quantity: 1,
            tax_behavior: "exclusive"
          });
        }
        
        // Ensure we have at least one valid line item
        if (!calculationData.line_items || calculationData.line_items.length === 0) {
          // Add a default line item if none exists
          calculationData.line_items = [{
            amount: 100, // Default $1
            quantity: 1,
            reference: "Default Product",
            tax_code: "txcd_99999999",
            tax_behavior: "exclusive"
          }];
        }
        
        // Validate all line items have required fields
        calculationData.line_items = calculationData.line_items.map((item, index) => {
          // Ensure all line items are valid objects
          if (typeof item !== 'object' || item === null) {
            logger.error(`Invalid line item at index ${index}`);
            return {
              amount: 100,
              quantity: 1,
              reference: "Product",
              tax_code: "txcd_99999999",
              tax_behavior: "exclusive"
            };
          }
          
          // Ensure amount is a positive integer
          const amount = typeof item.amount === 'number' ? item.amount : 100;
          
          // Return a plain object with all required fields
          return {
            amount: Math.max(1, Math.round(amount)),
            quantity: parseInt(item.quantity) || 1,
            reference: item.reference || "Product",
            tax_code: item.tax_code || "txcd_99999999",
            tax_behavior: "exclusive"
          };
        });
        
        logger.log('Sending calculation request:', JSON.stringify(calculationData, null, 2));
        
        // Call tax service API 
        const response = await taxService.calculateTax(calculationData);
        logger.log('Tax calculation response:', response);
        setResult(response);
      } catch (err) {
        logger.error('Tax calculation error in component:', err);
        // Log detailed error information
        if (err.response) {
          logger.error('Backend error response:', err.response.data);
          logger.error('Status code:', err.response.status);
        }
        
        // Format the error for display
        let errorMessage = '';
        if (err.response && err.response.data && err.response.data.detail) {
          errorMessage = typeof err.response.data.detail === 'string' 
            ? err.response.data.detail 
            : JSON.stringify(err.response.data.detail);
        } else if (err.message) {
          errorMessage = err.message;
        } else {
          errorMessage = 'An unknown error occurred during tax calculation';
        }
        
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  });

  const addItem = () => {
    const newItems = [...formik.values.items];
    newItems.push({
      description: '',
      quantity: 1,
      unitPrice: '',
      productCategory: 'general',
      taxCode: '',
      discount: 0,
      taxable: true
    });
    formik.setFieldValue('items', newItems);
  };

  const removeItem = (index) => {
    if (formik.values.items.length > 1) {
      const newItems = [...formik.values.items];
      newItems.splice(index, 1);
      formik.setFieldValue('items', newItems);
    }
  };

  const calculateSubtotal = () => {
    return formik.values.items.reduce((sum, item) => {
      return sum + (item.quantity * (item.unitPrice || 0) * (1 - (item.discount || 0) / 100));
    }, 0);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Tax Calculator
      </Typography>
      <Typography variant="body1" sx={{ mb: 4 }}>
        Enter transaction details to calculate accurate sales tax based on your nexus status and product taxability rules.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                {/* Transaction Details */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Transaction Details
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    name="transactionType"
                    label="Transaction Type"
                    value={formik.values.transactionType}
                    onChange={formik.handleChange}
                  >
                    <MenuItem value="sale">Sale</MenuItem>
                    <MenuItem value="return">Return</MenuItem>
                    <MenuItem value="exchange">Exchange</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="transactionDate"
                    label="Transaction Date"
                    type="date"
                    value={formik.values.transactionDate}
                    onChange={formik.handleChange}
                    error={formik.touched.transactionDate && Boolean(formik.errors.transactionDate)}
                    helperText={formik.touched.transactionDate && formik.errors.transactionDate}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                {/* Ship To Address */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Ship To Address
                    <Tooltip title="This address determines which tax jurisdictions apply">
                      <IconButton size="small" sx={{ ml: 1 }}>
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="shipToAddress.street"
                    label="Street Address"
                    value={formik.values.shipToAddress.street}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.shipToAddress?.street && 
                      Boolean(formik.errors.shipToAddress?.street)
                    }
                    helperText={
                      formik.touched.shipToAddress?.street && 
                      formik.errors.shipToAddress?.street
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    name="shipToAddress.city"
                    label="City"
                    value={formik.values.shipToAddress.city}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.shipToAddress?.city && 
                      Boolean(formik.errors.shipToAddress?.city)
                    }
                    helperText={
                      formik.touched.shipToAddress?.city && 
                      formik.errors.shipToAddress?.city
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={3} md={4}>
                  <TextField
                    fullWidth
                    name="shipToAddress.state"
                    label="State"
                    value={formik.values.shipToAddress.state}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.shipToAddress?.state && 
                      Boolean(formik.errors.shipToAddress?.state)
                    }
                    helperText={
                      formik.touched.shipToAddress?.state && 
                      formik.errors.shipToAddress?.state
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={3} md={4}>
                  <TextField
                    fullWidth
                    name="shipToAddress.zipCode"
                    label="ZIP Code"
                    value={formik.values.shipToAddress.zipCode}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.shipToAddress?.zipCode && 
                      Boolean(formik.errors.shipToAddress?.zipCode)
                    }
                    helperText={
                      formik.touched.shipToAddress?.zipCode && 
                      formik.errors.shipToAddress?.zipCode
                    }
                  />
                </Grid>

                {/* Line Items */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Line Items
                  </Typography>
                </Grid>

                {formik.values.items.map((item, index) => (
                  <Grid item xs={12} key={index}>
                    <Card variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              name={`items[${index}].description`}
                              label="Description"
                              value={item.description}
                              onChange={formik.handleChange}
                              error={
                                formik.touched.items?.[index]?.description && 
                                Boolean(formik.errors.items?.[index]?.description)
                              }
                              helperText={
                                formik.touched.items?.[index]?.description && 
                                formik.errors.items?.[index]?.description
                              }
                            />
                          </Grid>
                          
                          <Grid item xs={6} sm={3}>
                            <TextField
                              fullWidth
                              name={`items[${index}].quantity`}
                              label="Quantity"
                              type="number"
                              value={item.quantity}
                              onChange={formik.handleChange}
                              error={
                                formik.touched.items?.[index]?.quantity && 
                                Boolean(formik.errors.items?.[index]?.quantity)
                              }
                              helperText={
                                formik.touched.items?.[index]?.quantity && 
                                formik.errors.items?.[index]?.quantity
                              }
                              InputProps={{ inputProps: { min: 1 } }}
                            />
                          </Grid>
                          
                          <Grid item xs={6} sm={3}>
                            <TextField
                              fullWidth
                              name={`items[${index}].unitPrice`}
                              label="Unit Price"
                              type="number"
                              value={item.unitPrice}
                              onChange={formik.handleChange}
                              error={
                                formik.touched.items?.[index]?.unitPrice && 
                                Boolean(formik.errors.items?.[index]?.unitPrice)
                              }
                              helperText={
                                formik.touched.items?.[index]?.unitPrice && 
                                formik.errors.items?.[index]?.unitPrice
                              }
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                inputProps: { min: 0, step: 0.01 }
                              }}
                            />
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              select
                              name={`items[${index}].productCategory`}
                              label="Category"
                              value={item.productCategory}
                              onChange={formik.handleChange}
                              error={
                                formik.touched.items?.[index]?.productCategory && 
                                Boolean(formik.errors.items?.[index]?.productCategory)
                              }
                              helperText={
                                formik.touched.items?.[index]?.productCategory && 
                                formik.errors.items?.[index]?.productCategory
                              }
                            >
                              {productCategories.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                          
                          <Grid item xs={6} sm={3}>
                            <TextField
                              fullWidth
                              name={`items[${index}].discount`}
                              label="Discount"
                              type="number"
                              value={item.discount}
                              onChange={formik.handleChange}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                inputProps: { min: 0, max: 100 }
                              }}
                            />
                          </Grid>
                          
                          <Grid item xs={1} sm={1} display="flex" justifyContent="center" alignItems="center">
                            <IconButton 
                              color="error" 
                              onClick={() => removeItem(index)}
                              disabled={formik.values.items.length <= 1}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}

                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={addItem}
                    variant="outlined"
                  >
                    Add Item
                  </Button>
                </Grid>

                {/* Shipping and Handling */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Additional Charges
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="shipping"
                    label="Shipping"
                    type="number"
                    value={formik.values.shipping}
                    onChange={formik.handleChange}
                    error={formik.touched.shipping && Boolean(formik.errors.shipping)}
                    helperText={formik.touched.shipping && formik.errors.shipping}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      inputProps: { min: 0, step: 0.01 }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="handling"
                    label="Handling"
                    type="number"
                    value={formik.values.handling}
                    onChange={formik.handleChange}
                    error={formik.touched.handling && Boolean(formik.errors.handling)}
                    helperText={formik.touched.handling && formik.errors.handling}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      inputProps: { min: 0, step: 0.01 }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1">
                      Subtotal: ${calculateSubtotal().toFixed(2)}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={loading}
                      size="large"
                      sx={{ minWidth: 150 }}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Calculate Tax'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>

        {/* Results Panel */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Tax Calculation Result
              <Tooltip title="This shows the calculated tax based on the transaction details">
                <IconButton size="small" sx={{ ml: 1 }}>
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Typography>

            {error && (
              <ErrorDisplay error={error} />
            )}

            {result ? (
              <>
                <Box sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Subtotal
                  </Typography>
                  <Typography variant="h6">
                    ${result.subtotal.toFixed(2)}
                  </Typography>
                </Box>
                
                <Divider />
                
                <Box sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Tax Rate
                  </Typography>
                  <Typography variant="h6">
                    {(result.effectiveRate * 100).toFixed(2)}%
                  </Typography>
                </Box>
                
                <Divider />
                
                <Box sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Sales Tax
                  </Typography>
                  <Typography variant="h6" color="secondary.main">
                    ${result.taxAmount.toFixed(2)}
                  </Typography>
                </Box>
                
                <Divider />
                
                <Box sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Shipping & Handling
                  </Typography>
                  <Typography variant="h6">
                    ${(result.shipping + result.handling).toFixed(2)}
                  </Typography>
                </Box>
                
                <Divider />
                
                <Box sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Total
                  </Typography>
                  <Typography variant="h4" color="primary.main">
                    ${result.total.toFixed(2)}
                  </Typography>
                  {result.engine && (
                    <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', mt: 1 }}>
                      Calculated by GoTruTax
                    </Typography>
                  )}
                </Box>

                {result.jurisdictions && result.jurisdictions.length > 0 && (
                  <>
                    <Divider />
                    <Box sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Tax Breakdown
                      </Typography>
                      {result.jurisdictions.map((jurisdiction, index) => (
                        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">
                            {jurisdiction.name} ({jurisdiction.type})
                          </Typography>
                          <Typography variant="body2">
                            ${jurisdiction.taxAmount.toFixed(2)} ({(jurisdiction.rate * 100).toFixed(2)}%)
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}

                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                  >
                    Save Transaction
                  </Button>
                </Box>
              </>
            ) : (
              <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 2 }}>
                  Enter transaction details and click "Calculate Tax" to see your tax calculation.
                </Typography>
                <InfoIcon color="info" sx={{ fontSize: 64, opacity: 0.3 }} />
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TaxCalculator; 